<template>
  <div class="chat-controller">
    <!-- 表情选择器 -->
    <div v-if="showEmojiPicker" class="emoji-picker">
      <div class="emoji-header">
        <span>选择表情</span>
        <button @click="showEmojiPicker = false" class="close-emoji">✕</button>
      </div>
      <div class="emoji-grid">
        <span
          v-for="emoji in commonEmojis"
          :key="emoji"
          @click="insertEmoji(emoji)"
          class="emoji-item"
        >
          {{ emoji }}
        </span>
      </div>
    </div>

    <!-- 输入区域 -->
    <div class="input-area">
      <!-- 表情按钮 -->
      <button
        class="action-btn emoji-btn"
        @click="toggleEmojiPicker"
        title="选择表情"
      >
        😊
      </button>

      <!-- 文本输入框 -->
      <div class="textarea-wrapper">
        <textarea
          v-model="userInput"
          placeholder="有什么我可以帮你的吗？"
          @input="handleInput"
          @keydown.enter.prevent="sendMessage"
          :disabled="isProcessing"
          :style="{ minHeight: '44px', maxHeight: '120px' }"
        ></textarea>
        <!-- 字数统计 -->
        <div v-if="userInput.length > 0" class="char-count">
          {{ userInput.length }}
        </div>
      </div>

      <!-- 发送按钮 -->
      <button
        class="send-btn mimo-button"
        @click="sendMessage"
        :disabled="!userInput.trim() && !isProcessing"
      >
        <span v-if="isProcessing" class="sending-indicator">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="send-text">重新发送</span>
        </span>
        <span v-else class="send-text">
          发送<span class="send-icon">➤</span>
        </span>
      </button>
    </div>
    
    <!-- 快捷回复建议 -->
    <div v-if="showSuggestions" class="suggestions">
      <button
        v-for="(suggestion, index) in suggestions"
        :key="index"
        @click="useSuggestion(suggestion)"
        class="suggestion-btn"
      >
        {{ suggestion }}
      </button>
    </div>

    <!-- 快捷表情栏 -->
    <div v-if="!userInput" class="quick-emojis">
      <span
        v-for="emoji in quickEmojis"
        :key="emoji"
        @click="insertEmoji(emoji)"
        class="quick-emoji"
      >
        {{ emoji }}
      </span>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    lastUserMessage: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      userInput: '',
      isProcessing: false,
      showSuggestions: false,
      showEmojiPicker: false,
      suggestions: [
        '推荐一些好吃的',
        '讲个笑话',
        '推荐一些电影',
        '陪陪我',
        '我不开心'
      ],
      // 常用表情
      commonEmojis: ['😊', '😂', '❤️', '👍', '🎉', '🔥', '✨', '💯', '🙏', '😎', '🤔', '🥰', '👏', '💪', '🌟', '🎈', '🎁', '🎊', '🎯', '💡'],
      // 快捷表情
      quickEmojis: ['😊', '👍', '❤️', '🎉', '✨']
    }
  },
  
  methods: {
    handleInput() {
      // 根据输入长度调整建议显示
      if (this.userInput.length === 0) {
        this.showSuggestions = true;
      } else {
        this.showSuggestions = false;
      }
    },
    
    async sendMessage() {
      // 如果正在处理，点击按钮则重新发送上一条消息
      if (this.isProcessing && this.lastUserMessage) {
        this.$emit('send', this.lastUserMessage);
        return;
      }
      
      if (!this.userInput.trim() || this.isProcessing) return;
      
      const message = this.userInput.trim();
      this.userInput = '';
      this.isProcessing = true;
      this.showSuggestions = false;
      this.showEmojiPicker = false;
      
      // 触发发送事件
      this.$emit('send', message);
      
      // 模拟思考延迟，体现Mimo的"贴心"感
      await new Promise(resolve => setTimeout(resolve, 800));
      
      this.isProcessing = false;
    },
    
    useSuggestion(text) {
      this.userInput = text;
      this.showSuggestions = false;
      this.$nextTick(() => {
        this.sendMessage();
      });
    },
    
    // 切换表情选择器
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
      if (this.showEmojiPicker) {
        this.showSuggestions = false;
      }
    },
    
    // 插入表情
    insertEmoji(emoji) {
      this.userInput += emoji;
      this.showEmojiPicker = false;
      // 聚焦到输入框
      this.$nextTick(() => {
        const textarea = this.$el.querySelector('textarea');
        if (textarea) {
          textarea.focus();
          // 将光标移到末尾
          const len = this.userInput.length;
          textarea.setSelectionRange(len, len);
        }
      });
    }
  },
  
  mounted() {
    this.showSuggestions = true;
  }
}
</script>

<style scoped>
.chat-controller {
  border-top: 1px solid rgba(255, 105, 0, 0.1);
  background: rgba(255, 255, 255, 0.20);
  padding: 16px;
  position: relative;
}

/* 表情选择器 */
.emoji-picker {
  position: absolute;
  bottom: 100%;
  left: 16px;
  right: 16px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  padding: 12px;
  margin-bottom: 8px;
  z-index: 1000;
  animation: slideUp 0.3s ease;
}

.emoji-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 8px;
}

.emoji-header span {
  font-weight: 600;
  color: #333;
}

.close-emoji {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #999;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-emoji:hover {
  background: #f5f5f5;
  color: #333;
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.emoji-item {
  font-size: 24px;
  cursor: pointer;
  text-align: center;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
  display: block;
}

.emoji-item:hover {
  background: #f0f0f0;
  transform: scale(1.2);
}

/* 输入区域 */
.input-area {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  background: white;
  border-radius: 20px;
  padding: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.input-area:focus-within {
  border-color: #FF6900;
  box-shadow: 0 4px 20px rgba(255, 105, 0, 0.15);
}

/* 动作按钮 */
.action-btn {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  width: 44px;
  height: 44px;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.action-btn:hover {
  background: #FF6900;
  color: white;
  border-color: #FF6900;
  transform: translateY(-2px);
}

/* 文本输入框包装器 */
.textarea-wrapper {
  flex: 1;
  position: relative;
  min-height: 44px;
}

textarea {
  width: 100%;
  border: none;
  background: transparent;
  padding: 8px 12px;
  font-size: 16px;
  resize: none;
  min-height: 44px;
  max-height: 120px;
  font-family: inherit;
  line-height: 1.5;
}

textarea:focus {
  outline: none;
}

textarea:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
  border-radius: 8px;
}

/* 字数统计 */
.char-count {
  position: absolute;
  bottom: 4px;
  right: 8px;
  font-size: 12px;
  color: #999;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 6px;
  border-radius: 10px;
  pointer-events: none;
}

/* 发送按钮 */
.send-btn {
  padding: 12px 20px;
  white-space: nowrap;
  min-width: 70px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.send-text {
  display: flex;
  align-items: center;
  gap: 4px;
}

.send-icon {
  font-size: 14px;
  opacity: 0.8;
}

/* 思考中指示器 */
.sending-indicator {
  display: flex;
  gap: 4px;
  align-items: center;
}

.dot {
  width: 4px;
  height: 4px;
  background: white;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out;
}

.dot:nth-child(1) { animation-delay: -0.32s; }
.dot:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

/* 快捷建议 */
.suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  animation: fadeIn 0.3s ease;
}

.suggestion-btn {
  background: #FFF8F3;
  border: 1px solid rgba(255, 105, 0, 0.2);
  border-radius: 20px;
  padding: 8px 16px;
  color: #FF6900;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
}

.suggestion-btn:hover {
  background: #FF6900;
  color: white;
  border-color: #FF6900;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 105, 0, 0.3);
}

/* 快捷表情栏 */
.quick-emojis {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 0;
  animation: fadeIn 0.3s ease;
}

.quick-emoji {
  font-size: 24px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 12px;
  transition: all 0.2s ease;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}

.quick-emoji:hover {
  background: #FF6900;
  transform: scale(1.2) translateY(-2px);
  border-color: #FF6900;
}

/* 现代化按钮 - 替换原有的mimo-button */
.mimo-button {
  background: linear-gradient(135deg, #FF6900 0%, #FF8C42 100%);
  border: none;
  border-radius: 16px;
  color: white;
  font-weight: 600;
  font-size: 15px;
  box-shadow: 0 4px 16px rgba(255, 105, 0, 0.3);
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.mimo-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.mimo-button:hover::before {
  left: 100%;
}

.mimo-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 105, 0, 0.4);
}

.mimo-button:active {
  transform: translateY(0) scale(0.98);
  box-shadow: 0 2px 12px rgba(255, 105, 0, 0.5);
}

.mimo-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.mimo-button:disabled:hover {
  transform: none;
  box-shadow: none;
}

.mimo-button:disabled::before {
  display: none;
}

/* 动画 */
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* 移动端适配 */
@media (max-width: 480px) {
  .chat-controller {
    padding: 12px;
  }
  
  .input-area {
    padding: 10px;
    gap: 8px;
  }
  
  .action-btn {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }
  
  .emoji-grid {
    grid-template-columns: repeat(6, 1fr);
  }
  
  .quick-emojis {
    gap: 6px;
  }
  
  .quick-emoji {
    font-size: 20px;
    padding: 4px 8px;
  }
  
  .send-btn {
    padding: 10px 16px;
    min-width: 60px;
  }
}
</style>